// selectUtils.js
// Utility functions for handling MUI Select component validation and data extraction

/**
 * Validates that a select value exists in the available options
 * @param {any} value - The value to validate
 * @param {Array} options - Array of available options
 * @returns {string} - Valid value or empty string
 */
export const validateSelectValue = (value, options) => {
  if (!value || !options || !Array.isArray(options)) return '';
  return options.includes(value) ? value : '';
};

/**
 * Extracts ID from value that could be string or object
 * @param {any} value - The value to extract ID from
 * @returns {string} - Extracted ID or empty string
 */
export const extractId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value._id || value.id || '';
  }
  return '';
};

/**
 * Safely gets nested object property for vehicle models
 * @param {Object} modelsObj - Object containing models by make
 * @param {string} make - The make to get models for
 * @returns {Array} - Array of models or empty array
 */
export const getModelsForMake = (modelsObj, make) => {
  if (!modelsObj || !make || typeof modelsObj !== 'object') return [];
  return modelsObj[make] || [];
};

/**
 * Checks if vehicle data is ready for use
 * @param {Object} vehicleData - Vehicle data object
 * @returns {boolean} - True if data is ready
 */
export const isVehicleDataReady = (vehicleData) => {
  return vehicleData && 
         vehicleData.makes && 
         Array.isArray(vehicleData.makes) &&
         vehicleData.models && 
         typeof vehicleData.models === 'object' &&
         vehicleData.colors && 
         Array.isArray(vehicleData.colors) &&
         vehicleData.years && 
         Array.isArray(vehicleData.years);
};

/**
 * Validates and cleans vehicle data values
 * @param {Object} jobData - Current job data
 * @param {Object} vehicleData - Available vehicle options
 * @returns {Object} - Object with validated values
 */
export const validateVehicleData = (jobData, vehicleData) => {
  if (!isVehicleDataReady(vehicleData) || !jobData) {
    return {
      make: '',
      model: '',
      color: '',
      year: ''
    };
  }

  const validMake = validateSelectValue(jobData.make, vehicleData.makes);
  const availableModels = getModelsForMake(vehicleData.models, validMake);
  const validModel = validMake ? validateSelectValue(jobData.model, availableModels) : '';
  const validColor = validateSelectValue(jobData.color, vehicleData.colors);
  const validYear = validateSelectValue(jobData.year, vehicleData.years);

  return {
    make: validMake,
    model: validModel,
    color: validColor,
    year: validYear
  };
};